import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import { Provider } from 'react-redux';
import { store, persistor } from './config/store';
import { PersistGate } from 'redux-persist/integration/react';
import { useDispatch } from 'react-redux';
import { setDomainId } from './features/bookingSlice/bookingSlice';
import { useEffect } from 'react';

// Import test functions for browser console access
import { runAllTests, testComplete6StepFlow, testStepByStep, testFleetFetching } from './api/testBookingApi';
import 'bootstrap/dist/css/bootstrap.min.css';

const DOMAIN_ID_MAP = {
  'gobostonloganairport.com': 1,
  'bostonairportexpresscar.com': 2,
  'airporttaxima.com': 3,
  'bostonairportcheapcar.com': 4,
  'loganairportcar.com': 5,
  'bostonloganairportcab.com': 6,
  'natickairporttaxi.com': 7,
  'concordairporttaxi.com': 8,
  'airportcarlexington.com': 9,
  'walthamairporttaxi.com': 10,
  'airporttaxiarlington.com': 11,
  'airporttaxicambridgema.com': 12,
  'airporttaxiandover.com': 13,
  'newtonairporttaxima.com': 14,
  'actonairporttaxi.com': 15,
  'hudsonairporttaxi.com': 16,
  'bosairportlimos.com': 17,
  'westonairporttaxima.com': 18,
  'wilmingtonairporttaxis.com': 19,
  'bostonairportcarma.com': 20,
  'bostonairporttaxis.com': 21,
  'gologanairport.com': 22,
  'gobostonairport.com': 23,
  'worcesterairporttaxi.com': 24,
  'loganexpresscar.com': 25,
  'localhost': 99,
};

// Add a mapping from domain_id to title and description
const DOMAIN_META_MAP = {
  1: { title: 'Go Boston Logan Airport', description: 'Book your ride with Go Boston Logan Airport.' },
  2: { title: 'Boston Airport Express Car', description: 'Book your ride with Boston Airport Express Car.' },
  3: { title: 'Airport Taxi MA', description: 'Book your ride with Airport Taxi MA.' },
  4: { title: 'Boston Airport Cheap Car', description: 'Book your ride with Boston Airport Cheap Car.' },
  5: { title: 'Logan Airport Car', description: 'Book your ride with Logan Airport Car.' },
  6: { title: 'Boston Logan Airport Cab', description: 'Book your ride with Boston Logan Airport Cab.' },
  7: { title: 'Natick Airport Taxi', description: 'Book your ride with Natick Airport Taxi.' },
  8: { title: 'Concord Airport Taxi', description: 'Book your ride with Concord Airport Taxi.' },
  9: { title: 'Airport Car Lexington', description: 'Book your ride with Airport Car Lexington.' },
  10: { title: 'Waltham Airport Taxi', description: 'Book your ride with Waltham Airport Taxi.' },
  11: { title: 'Airport Taxi Arlington', description: 'Book your ride with Airport Taxi Arlington.' },
  12: { title: 'Airport Taxi Cambridge MA', description: 'Book your ride with Airport Taxi Cambridge MA.' },
  13: { title: 'Airport Taxi Andover', description: 'Book your ride with Airport Taxi Andover.' },
  14: { title: 'Newton Airport Taxi MA', description: 'Book your ride with Newton Airport Taxi MA.' },
  15: { title: 'Acton Airport Taxi', description: 'Book your ride with Acton Airport Taxi.' },
  16: { title: 'Hudson Airport Taxi', description: 'Book your ride with Hudson Airport Taxi.' },
  17: { title: 'BOS Airport Limos', description: 'Book your ride with BOS Airport Limos.' },
  18: { title: 'Weston Airport Taxi MA', description: 'Book your ride with Weston Airport Taxi MA.' },
  19: { title: 'Wilmington Airport Taxis', description: 'Book your ride with Wilmington Airport Taxis.' },
  20: { title: 'Boston Airport Car MA', description: 'Book your ride with Boston Airport Car MA.' },
  21: { title: 'Boston Airport Taxis', description: 'Book your ride with Boston Airport Taxis.' },
  22: { title: 'Go Logan Airport', description: 'Book your ride with Go Logan Airport.' },
  23: { title: 'Go Boston Airport', description: 'Book your ride with Go Boston Airport.' },
  24: { title: 'Worcester Airport Taxi', description: 'Book your ride with Worcester Airport Taxi.' },
  25: { title: 'Logan Express Car', description: 'Book your ride with Logan Express Car.' },
  99: { title: 'Localhost Booking', description: 'Book your ride locally for testing.' },
};

// Add a mapping from domain_id to booking reference prefix
const DOMAIN_BOOKING_PREFIX_MAP = {
  1: 'GOBOS',     // gobostonloganairport.com
  2: 'BAEC',     // bostonairportexpresscar.com
  3: 'ATM',     // airporttaxima.com
  4: 'BACC',     // bostonairportcheapcar.com
  5: 'LAC',     // loganairportcar.com
  6: 'BLAC',     // bostonloganairportcab.com
  7: 'NAT',     // natickairporttaxi.com
  8: 'CON',     // concordairporttaxi.com
  9: 'LEX',     // airportcarlexington.com
  10: 'WAL',    // walthamairporttaxi.com
  11: 'ARL',    // airporttaxiarlington.com
  12: 'CAM',    // airporttaxicambridgema.com
  13: 'ADOV',    // airporttaxiandover.com
  14: 'NEW',    // newtonairporttaxima.com
  15: 'AAT',    // actonairporttaxi.com
  16: 'HUD',    // hudsonairporttaxi.com
  17: 'BLIMO',    // bosairportlimos.com
  18: 'WEST',    // westonairporttaxima.com
  19: 'WAT',    // wilmingtonairporttaxis.com
  20: 'BACMA',    // bostonairportcarma.com
  21: 'BAT',    // bostonairporttaxis.com
  22: 'GLA',    // gologanairport.com
  23: 'GBA',    // gobostonairport.com
  24: 'WOC',    // worcesterairporttaxi.com
  25: 'LEC',    // loganexpresscar.com
  99: 'GLA',    // localhost
};

// Add a mapping from domain_id to favicon URL
const DOMAIN_FAVICON_MAP = {
  1: 'https://gobostonloganairport.com/assets/images/logo.png',
  2: 'https://bostonairportexpresscar.com/static/media/logo.88f461a2665014ffe1bc.png',
  3: 'logoDefault', // No external link provided
  4: 'https://bostonairportcheapcar.com/assets/images/logo.png',
  5: 'https://loganairportcar.com/assets/images/logo.png',
  6: 'https://bostonloganairportcab.com/assets/images/logo.png',
  7: 'https://natickairporttaxi.com/static/media/image1.7ed04203391456d2095e.png',
  8: 'https://concordairporttaxi.com/static/media/logo.d0dc22d4b1a265454993.png',
  9: 'https://lairportcarlexington.com/assets/img/logo.png', // No external link provided
  10: 'https://walthamairporttaxi.com/assets/image/footer-logo.png',
  11: 'https://airporttaxiarlington.com/assets/images/biz-logo.png',
  12: 'https://airporttaxicambridgema.com/assets/images/logo.png',
  13: 'logoDefault', // No external link provided
  14: 'https://newtonairporttaxima.com/assets/images/logo.png',
  15: 'https://actonairporttaxi.com/assets/images/nav_logo.png',
  16: 'logoDefault', // No external link provided
  17: 'https://bosairportlimos.com/assets/images/biz-logo.png',
  18: 'https://westonairporttaxima.com/assets/images/hdr-logo.png',
  19: 'https://wilmingtonairporttaxis.com/assets/images/logo.png',
  20: 'https://bostonairportcarma.com/assets/images/logo.png',
  21: 'https://bostonairporttaxis.com/assets/image/logo.png',
  22: 'https://dispatch.gologanairport.com/assets/images/logo-sm.png',
  23: 'https://gobostonairport.com/assets/images/logo.png',
  24: 'https://worcesterairporttaxi.com/images/Logo%20Blue.png',
  25: 'https://loganexpresscar.com/assets/images/logo.svg',
  99: '/favicon.ico', // Default for localhost
};

function getParentDomain() {
  const host = window.location.hostname;
  const parts = host.split('.');
  if (parts.length >= 3) {
    return parts.slice(1).join('.');
  }
  return host;
}

// Set title, description, and favicon based on domain_id
function setDomainMeta(domainId) {
  const meta = DOMAIN_META_MAP[domainId];
  if (meta) {
    document.title = meta.title;
    let descTag = document.querySelector('meta[name="description"]');
    if (!descTag) {
      descTag = document.createElement('meta');
      descTag.name = 'description';
      document.head.appendChild(descTag);
    }
    descTag.content = meta.description;
  }
  // Set favicon
  const faviconUrl = DOMAIN_FAVICON_MAP[domainId];
  if (faviconUrl) {
    let link = document.querySelector("link[rel~='icon']");
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    link.href = faviconUrl === 'logoDefault' ? '/favicon.ico' : faviconUrl;
    // Set type for PNG/SVG
    if (faviconUrl.endsWith('.png')) link.type = 'image/png';
    if (faviconUrl.endsWith('.svg')) link.type = 'image/svg+xml';
  }
}

// Export function to get booking prefix by domain ID
export function getBookingPrefixByDomainId(domainId) {
  return DOMAIN_BOOKING_PREFIX_MAP[domainId] || 'BK';
}

// Export function to get current domain's booking prefix
export function getCurrentDomainBookingPrefix() {
  const parentDomain = getParentDomain();
  const domainId = DOMAIN_ID_MAP[parentDomain] || 99;
  return getBookingPrefixByDomainId(domainId);
}

function DomainIdInitializer({ children }) {
  const dispatch = useDispatch();
  useEffect(() => {
    const host = window.location.hostname;
    const parentDomain = getParentDomain();
    const domainId = DOMAIN_ID_MAP[parentDomain] || null;
    
    // Debug logging
    console.log('🔍 Domain Detection Debug:');
    console.log('- Raw hostname:', host);
    console.log('- Parent domain:', parentDomain);
    console.log('- Domain ID:', domainId);
    console.log('- Available domains:', Object.keys(DOMAIN_ID_MAP));
    
    if (domainId) {
      dispatch(setDomainId(domainId));
      setDomainMeta(domainId);
      console.log('✅ Domain detected and set successfully');
    } else {
      console.error('❌ Domain detection failed - using fallback');
      // Fallback to localhost
      const fallbackId = 99;
      dispatch(setDomainId(fallbackId));
      setDomainMeta(fallbackId);
    }
  }, [dispatch]);
  return children;
}

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <Provider store={store}>
      <PersistGate loading={null} persistor={persistor}>
        <DomainIdInitializer>
          <App />
        </DomainIdInitializer>
      </PersistGate>
    </Provider>
  </React.StrictMode>
);

// Make test functions available in browser console
window.testBookingAPI = {
  runAllTests,
  testComplete6StepFlow,
  testStepByStep,
  testFleetFetching
};

console.log('🧪 NEW 6-STEP Booking API tests available in browser console:');
console.log('🗺️ WITH REAL GOOGLE MAPS INTEGRATION');
console.log('- window.testBookingAPI.runAllTests()');
console.log('- window.testBookingAPI.testComplete6StepFlow()');
console.log('- window.testBookingAPI.testStepByStep()');
console.log('🚗 FLEET TESTING:');
console.log('- window.testBookingAPI.testFleetFetching()');

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
